<template>
  <v-dialog
    v-model="show"
    :max-width="$vuetify.breakpoint.xs ? 'auto' : '500px'"
    persistent
  >
    <v-card class="rounded-xl" color="#ffffffD9">
      <v-card-title class="">
        <span
          class="
                primary--text
                text-subtitle-2
                text-sm-subtitle-1
                text-md-subtitle-1
                text-lg-subtitle-1
                text-xl-subtitle-1
              "
          >{{ translations.addMultipleSubsamples }}</span
        >
      </v-card-title>
      <v-card-text class="my-0 py-0">
        <v-file-input
          v-model="data.file"
          outlined
          accept="image/*, image/heic"
          clearable
          dense
          color="primary"
          @change="handleFileChange"
          :label="translations.selectImage"
          class="
                text-subtitle-2
                text-sm-subtitle-1
                text-md-subtitle-1
                text-lg-subtitle-1
                text-xl-subtitle-1
              "
          multiple
          chips
        ></v-file-input>
        <v-row>
          <v-col>
            <v-btn
              color="red"
              block
              dark
              class="
                mb-5
                text-subtitle-2
                text-sm-subtitle-1
                text-md-subtitle-1
                text-lg-subtitle-1
                text-xl-subtitle-1
              "
              @click="canceladdPhoto"
            >
              {{ translations.cancel }}
            </v-btn>
          </v-col>
          <v-col>
            <v-btn
              color="primary"
              block
              class="
                mb-5
                text-subtitle-2
                text-sm-subtitle-1
                text-md-subtitle-1
                text-lg-subtitle-1
                text-xl-subtitle-1
              "
              @click="addPhoto"
              :disabled="isLoading"
            >
              {{ translations.acept }}
            </v-btn>
          </v-col>
        </v-row>
        <v-row v-if="isLoading">
          <v-col>
            <v-progress-linear
              color="primary accent-4"
              indeterminate
              rounded
              height="6"
            ></v-progress-linear>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions></v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import { findEXIFinHEIC } from "./exif-heic.js";
import heic2any from "heic2any";
import EXIF from "exif-js";
import moment from "moment";
// Validations
import { requiredValidation } from "@/helpers/validations.js";
export default {
  props: {
    value: Boolean,
  },
  data: () => ({
    data: { error: false, message: "", file: null },
    isLoading: false,
    rules: null,
    filesWithMetadata: [],
  }),
  created() {
    this.rules = requiredValidation(this.translations.field_required);
  },
  computed: {
    ...mapState("samples", ["samplingPoints"]),
    ...mapGetters("session", ["currentTranslations"]),
    translations() {
      return this.currentTranslations;
    },
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
  },
  methods: {
    async handleFileChange(files) {
      if (!files) return;
      this.isLoading = true;
      for (const file of files) {
        let metadata = null;
        if (file) {
          if (file.type === "image/heic") {
            metadata = await this.extractExifDataHEIC(file);
          } else {
            metadata = await this.extractExifData(file);
          }
          this.filesWithMetadata.push(metadata);
        }
      }
      this.isLoading = false;
    },
    async convertHeicToJpeg(file) {
      const arrayBuffer = await this.fileToArrayBuffer(file);
      const convertedBuffer = await heicConvert({
        buffer: arrayBuffer,
        format: "JPEG",
        quality: 1,
      });
      return new Blob([convertedBuffer], { type: "image/jpeg" });
    },
    fileToArrayBuffer(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      });
    },
    async extractExifDataHEIC(file) {
      // Convert HEIC to JPEG
      const convertedBlob = await heic2any({
        blob: file,
        toType: "image/jpeg",
      });

      // Create a promise for the FileReader
      const readFile = () =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();

          reader.onload = () => resolve(reader.result);
          reader.onerror = () => reject(reader.error);

          reader.readAsArrayBuffer(file);
        });

      try {
        // Wait for the file reading to complete
        const fileData = await readFile();

        // Process EXIF ​​data
        let tags = findEXIFinHEIC(fileData);
        const dateTimeOriginal = tags["DateTimeOriginal"];
        const lat = tags["GPSLatitude"];
        const lon = tags["GPSLongitude"];
        const latRef = tags["GPSLatitudeRef"];
        const lonRef = tags["GPSLongitudeRef"];

        const latitude = lat ? this.convertToDecimal(lat, latRef) : null;
        const longitude = lon ? this.convertToDecimal(lon, lonRef) : null;

        let metadata = {
          dateTimeOriginal:
            dateTimeOriginal || moment().format("YYYY:MM:DD HH:mm:ss"),
          latitude,
          longitude,
          file: convertedBlob,
          error: false,
          fileName: file.name,
        };
        return metadata; // Returns the processed metadata
      } catch (error) {
        console.error("Error al leer el archivo:", error);
        return {
          error: true,
          fileName: file.name,
          file: file,
        };
      }
    },
    async extractExifData(file, metadata) {
      // Convert FileReader to promise
      const readFileAsDataURL = () =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (event) => resolve(event.target.result);
          reader.onerror = (error) => reject(error);
          reader.readAsDataURL(file);
        });

      // convert Image.onload to promise
      const loadImage = (src) =>
        new Promise((resolve, reject) => {
          const image = new Image();
          image.onload = () => resolve(image);
          image.onerror = (error) => reject(error);
          image.src = src;
        });

      try {
        // Read the file as DataURL
        const fileDataURL = await readFileAsDataURL();

        // Upload the image
        const image = await loadImage(fileDataURL);

        // Convert EXIF.getData to promise
        const getExifData = (img) =>
          new Promise((resolve) => {
            EXIF.getData(img, () => resolve(img));
          });

        // Get EXIF ​​data
        const exifImage = await getExifData(image);

        // Extract metadata
        const dateTimeOriginal = EXIF.getTag(exifImage, "DateTimeOriginal");
        const lat = EXIF.getTag(exifImage, "GPSLatitude");
        const lon = EXIF.getTag(exifImage, "GPSLongitude");
        const latRef = EXIF.getTag(exifImage, "GPSLatitudeRef");
        const lonRef = EXIF.getTag(exifImage, "GPSLongitudeRef");

        const latitude = lat ? this.convertToDecimal(lat, latRef) : null;
        const longitude = lon ? this.convertToDecimal(lon, lonRef) : null;

        metadata = {
          dateTimeOriginal:
            dateTimeOriginal || moment().format("YYYY:MM:DD HH:mm:ss"),
          latitude,
          longitude,
          file,
          error: false,
          fileName: file.name,
        };
        return metadata;
      } catch (error) {
        console.error("Error al procesar EXIF:", error);
        return {
          error: true,
          fileName: file.name,
          file: file,
        };
      }
    },
    convertToDecimal(coord, ref) {
      if (!coord) return null;
      const decimal = coord[0] + coord[1] / 60 + coord[2] / 3600;
      return ref === "S" || ref === "W" ? -decimal : decimal;
    },
    getFormattedSimple(date) {
      return date ? moment(date, "YYYY:MM:DD HH:mm:ss") : "----/--/--";
    },
    canceladdPhoto() {
      this.filesWithMetadata = [];
      this.show = false;
    },
    addPhoto() {
      this.$emit("addPhotos", this.filesWithMetadata);
    },
  },
};
</script>

<style scoped></style>
